import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Save,
  ArrowLeft,
  UserPlus,
  Trash2,
} from "react-feather";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getDepartmentAPI,
  updateDepartmentAPI,
  getUsersAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import StatusBadge from "../../../Components/shared/StatusBadge";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./DepartmentDetail.module.scss";

export default function DepartmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dept, setDept] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [deptRes, usersRes] = await Promise.all([
        getDepartmentAPI(id),
        getUsersAPI(),
      ]);
      setDept(deptRes.data);
      setUsers(usersRes.data || []);
      setSelectedManagerId(deptRes.data?.manager?.userId || "");
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const [form, setForm] = useState({
    name: "",
    status: "active",
    manager: null,
  });

  useEffect(() => {
    if (dept) {
      setForm({
        name: dept.name || "",
        status: dept.status || "active",
        manager: dept.manager || null,
      });
      setSelectedManagerId(dept.manager?.userId || "");
    }
  }, [dept]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const selectedUser = users.find((u) => u.userId === selectedManagerId);
      const updateData = {
        name: form.name,
        status: form.status,
        manager: selectedManagerId
          ? {
              userId: selectedUser.userId,
              name: selectedUser.name,
              email: selectedUser.email,
            }
          : null,
      };
      await updateDepartmentAPI(id, updateData);
      toast.success("Department updated successfully");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDepartmentAPI(id, { status: newStatus });
      toast.success(
        `Department ${newStatus === "active" ? "activated" : "inactivated"}`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  if (loading && !dept) {
    return (
      <div>
        <PageHeader
          title="Department Details"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Departments", path: companyUrl("departments") },
            { label: "Loading..." },
          ]}
        />
        <LoadingSkeleton rows={6} cols={2} />
      </div>
    );
  }

  if (error && !dept) {
    return (
      <div>
        <PageHeader
          title="Department Details"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Departments", path: companyUrl("departments") },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Department not found"
          description={error.message}
          actionLabel="Back to Departments"
          onAction={() => navigate(companyUrl("departments"))}
        />
      </div>
    );
  }

  if (!dept) return null;

  const managerOptions = users.map((user) => ({
    value: user.userId,
    label: `${user.name} (${user.email})`,
  }));

  return (
    <div className={styles.page}>
      <PageHeader
        title={dept.name}
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Departments", path: companyUrl("departments") },
          { label: dept.name },
        ]}
        secondaryActions={[
          {
            label: "Back to Departments",
            icon: ArrowLeft,
            onClick: () => navigate(companyUrl("departments")),
          },
        ]}
      />

      <div className={styles.content}>
        {/* Basic Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>Basic Information</h3>
            <StatusBadge status={dept.status} />
          </div>
          <div className={styles.cardBody}>
            <div className={styles.formGrid}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Department Name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Department name"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Department ID</label>
                <input
                  type="text"
                  className={`${styles.input} ${styles.readonly}`}
                  value={dept.departmentId}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>
          <div className={styles.cardFooter}>
            <button
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={16} />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Metadata Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>Metadata</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.metadataGrid}>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Created</span>
                <span className={styles.metadataValue}>
                  {new Date(dept.createdAt).toLocaleString()}
                </span>
              </div>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Last Updated</span>
                <span className={styles.metadataValue}>
                  {new Date(dept.updatedAt).toLocaleString()}
                </span>
              </div>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Tenant ID</span>
                <span className={styles.metadataValue}>{dept.tenantId}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Users in Department Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>Users in this Department</h3>
            <button
              className={styles.iconBtn}
              onClick={() =>
                toast.info(
                  "Navigate to Users page to manage department assignments",
                )
              }
            >
              <UserPlus size={16} />
            </button>
          </div>
          <div className={styles.cardBody}>
            {dept.users && dept.users.length > 0 ? (
              <div className={styles.userList}>
                {dept.users.map((user, idx) => (
                  <div key={idx} className={styles.userItem}>
                    <div className={styles.userAvatar}>
                      {user.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{user.name}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                    </div>
                    <div className={styles.userRole}>
                      {user.roles[0] || "Member"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyUsers}>
                <p>No users assigned to this department yet.</p>
                <button
                  className={styles.btnOutline}
                  onClick={() => navigate(companyUrl("users"))}
                >
                  Go to Users
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
